/* ===========================================================
   Zemoria Oil — Theme JS
   Handles: cart drawer, AJAX add-to-cart, variant selection,
            quantity steppers, mobile menu, gallery thumbnails
   =========================================================== */

(function () {
  'use strict';

  /* ---------- Utils ---------- */
  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  /* Format money using Shopify's format (falls back to €X,XX) */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    if (!format) format = '€{{amount_with_comma_separator}}';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return format.replace('{{amount_with_comma_separator}}', amount)
                 .replace('{{amount}}', (cents / 100).toFixed(2));
  }

  /* ---------- Fetch wrapper for Shopify cart.js ---------- */
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function addToCart(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: formData
    });
    if (!res.ok) {
      // Shopify returns { message, description, status } on error
      let errInfo = {};
      try { errInfo = await res.json(); } catch (e) { errInfo = {}; }
      console.error('[Zemoria] Add to cart failed:', res.status, errInfo);
      // Surface the actual Shopify error message so the user knows what's wrong
      let msg = errInfo.description || errInfo.message || '';
      if (res.status === 422) {
        // 422 = Unprocessable Entity — usually means variant is unavailable/not purchasable
        if (!msg) msg = 'Ce produit n’est pas disponible pour le moment.';
      }
      if (!msg) msg = 'HTTP ' + res.status + ' — could not add to cart';
      throw new Error(msg);
    }
    return res.json();
  }

  async function changeCart(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!res.ok) throw new Error('Cart update failed');
    return res.json();
  }

  async function setVariantQuantity(variantId, quantity) {
    const currentCart = await fetchCart();
    const existingIndex = currentCart.items.findIndex(item => String(item.variant_id) === String(variantId));

    if (existingIndex >= 0) {
      return changeCart(existingIndex + 1, quantity);
    }

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(quantity));
    await addToCart(formData);
    return fetchCart();
  }

  /* ---------- Cart Drawer ---------- */
  const drawer = $('#cart-drawer');
  const drawerBody = drawer && $('[data-cart-drawer-body]', drawer);
  const drawerItems = drawer && $('[data-cart-items]', drawer);
  const drawerEmpty = drawer && $('[data-cart-empty]', drawer);
  const drawerFooter = drawer && $('[data-cart-footer]', drawer);
  const drawerSubtotal = drawer && $('[data-cart-subtotal]');
  const cartCountEls = $$('[data-cart-count]');

  function openDrawer() {
    if (!drawer) return (window.location.href = '/cart');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    renderCart();
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async function renderCart() {
    try {
      const cart = await fetchCart();
      updateCount(cart.item_count);
      updateSubtotal(cart.total_price);

      if (cart.item_count === 0) {
        if (drawerEmpty) drawerEmpty.hidden = false;
        if (drawerItems) drawerItems.hidden = true;
        if (drawerFooter) drawerFooter.hidden = true;
        return;
      }
      if (drawerEmpty) drawerEmpty.hidden = true;
      if (drawerItems) {
        drawerItems.hidden = false;
        drawerItems.innerHTML = cart.items.map(renderDrawerItem).join('');
        wireDrawerItemActions();
      }
      if (drawerFooter) drawerFooter.hidden = false;
    } catch (e) {
      console.error(e);
    }
  }

  function renderDrawerItem(item, index) {
    const line = index + 1;
    const img = item.image
      ? `<img src="${item.image.replace('.jpg', '_120x.jpg').replace('.png', '_120x.png')}" alt="${escapeAttr(item.title)}" class="cart-drawer__item-image">`
      : '<div class="cart-drawer__item-image cart-line__image--placeholder"></div>';
    return `
      <div class="cart-drawer__item" data-drawer-line="${line}">
        ${img}
        <div>
          <a href="${item.url}" class="cart-drawer__item-title">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</p>` : ''}
          <div class="qty-stepper qty-stepper--small cart-drawer__item-qty" data-qty-stepper>
            <button type="button" class="qty-stepper__btn" data-qty-decrement data-drawer-line="${line}" aria-label="−">−</button>
            <input type="number" class="qty-stepper__input" data-qty-input data-drawer-line="${line}" value="${item.quantity}" min="0" aria-label="Quantité">
            <button type="button" class="qty-stepper__btn" data-qty-increment data-drawer-line="${line}" aria-label="+">+</button>
          </div>
          <button type="button" class="cart-drawer__item-remove" data-drawer-remove="${line}">Retirer</button>
        </div>
        <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
      </div>
    `;
  }

  function wireDrawerItemActions() {
    if (!drawerItems) return;
    $$('[data-drawer-remove]', drawerItems).forEach(btn => {
      on(btn, 'click', async () => {
        const line = parseInt(btn.dataset.drawerRemove, 10);
        await changeCart(line, 0);
        renderCart();
      });
    });
    $$('[data-qty-input]', drawerItems).forEach(input => {
      on(input, 'change', async () => {
        const line = parseInt(input.dataset.drawerLine, 10);
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        await changeCart(line, qty);
        renderCart();
      });
    });
    $$('[data-qty-decrement]', drawerItems).forEach(btn => {
      on(btn, 'click', async () => {
        const line = parseInt(btn.dataset.drawerLine, 10);
        const input = $(`[data-qty-input][data-drawer-line="${line}"]`, drawerItems);
        const qty = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        await changeCart(line, qty);
        renderCart();
      });
    });
    $$('[data-qty-increment]', drawerItems).forEach(btn => {
      on(btn, 'click', async () => {
        const line = parseInt(btn.dataset.drawerLine, 10);
        const input = $(`[data-qty-input][data-drawer-line="${line}"]`, drawerItems);
        const qty = (parseInt(input.value, 10) || 0) + 1;
        await changeCart(line, qty);
        renderCart();
      });
    });
  }

  function updateCount(count) {
    cartCountEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  function updateSubtotal(cents) {
    $$('[data-cart-subtotal]').forEach(el => {
      el.innerHTML = formatMoney(cents);
    });
  }

  /* ---------- Fixed-quantity offers (2 L / 4 L) ---------- */
  $$('[data-fixed-quantity-form]').forEach(form => {
    on(form, 'submit', async (event) => {
      event.preventDefault();
      const button = $('[data-fixed-quantity-button]', form);
      if (!button || button.dataset.pending === 'true') return;

      const variantInput = $('input[name="id"]', form);
      const variantId = variantInput && variantInput.value;
      const quantity = Math.max(1, parseInt(form.dataset.fixedQuantity, 10) || 1);
      if (!variantId) return;
      const originalHtml = button.innerHTML;

      button.dataset.pending = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = quantity === 2 ? 'PRÉPARATION DE L’OFFRE 4 L…' : 'AJOUT DU BIDON…';

      try {
        const cart = await setVariantQuantity(variantId, quantity);
        updateCount(cart.item_count);
        window.location.assign('/cart');
      } catch (error) {
        console.error(error);
        button.textContent = 'ERREUR — RÉESSAYER';
        setTimeout(() => {
          button.innerHTML = originalHtml;
          button.disabled = false;
          button.dataset.pending = 'false';
          button.removeAttribute('aria-busy');
        }, 1800);
      }
    });
  });

  /* ---------- Quick add to cart (product cards) ---------- */
  $$('[data-quick-add]').forEach(form => {
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const btn = $('[data-add-button]', form);
      const label = $('[data-add-label]', btn);
      const card = form.closest('[data-product-card]');
      const original = label ? label.textContent : '';
      if (btn) { btn.disabled = true; }
      if (label) label.textContent = '...';

      try {
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const returnTo = formData.get('return_to'); // '/checkout' for Buy Now, null for Add to cart

        if (!variantId) {
          throw new Error('Produit indisponible. Veuillez ouvrir la fiche produit.');
        }
        await addToCart(formData);

        // Buy Now: redirect to checkout. Add to cart: open drawer.
        if (returnTo === '/checkout') {
          if (label) label.textContent = '✓ Redirection...';
          window.location.href = '/checkout';
          return;
        }
        if (label) label.textContent = '✓ Ajouté';
        setTimeout(openDrawer, 250);
      } catch (err) {
        console.error('[Zemoria] Quick add FAILED:', err);
        if (label) label.textContent = '✗';
        const productInfo = card ? `\n\nProduct: ${card.dataset.productTitle}\nVariant: ${card.dataset.variantTitle}\nVariant ID: ${card.dataset.variantId}` : '';
        alert(err.message + productInfo);
      } finally {
        setTimeout(() => {
          if (btn) btn.disabled = false;
          if (label) label.textContent = original;
        }, 3000);
      }
    });
  });

  /* ---------- Product page form ---------- */
  // Two clearly distinct actions:
  //   - "Acheter maintenant" (Buy Now)   → form submit with return_to=/checkout → redirects to /checkout
  //   - "Ajouter au panier" (Add to cart) → form submit without return_to       → opens the cart drawer
  // We track WHICH button was clicked via a closure variable, and read it in the submit handler.
  const productForm = $('[data-product-form]');
  if (productForm) {
    const buyNowBtn     = $('[data-buy-now]', productForm);          // primary submit button
    const addToCartBtn  = $('[data-add-to-cart-only]', productForm); // secondary ghost button
    const buyNowFlag    = $('[data-buy-now-flag]', productForm);     // hidden input name="return_to"
    const messageEl     = $('[data-form-message]');
    let pendingAction = 'buy-now'; // default

    // When user clicks "Ajouter au panier", remove the return_to flag and trigger the form submit.
    if (addToCartBtn) {
      on(addToCartBtn, 'click', () => {
        pendingAction = 'add-to-cart';
        if (buyNowFlag) buyNowFlag.value = '';       // strip the redirect flag
        // Trigger form submit programmatically (form.requestSubmit fires the submit event)
        if (typeof productForm.requestSubmit === 'function') {
          productForm.requestSubmit();
        } else {
          productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    }

    // When user clicks "Acheter maintenant" directly, restore the return_to flag (in case
    // the previous click stripped it).
    if (buyNowBtn) {
      on(buyNowBtn, 'click', () => {
        pendingAction = 'buy-now';
        if (buyNowFlag) buyNowFlag.value = '/checkout';
      });
    }

    on(productForm, 'submit', async (e) => {
      e.preventDefault();
      const btn = pendingAction === 'buy-now' ? buyNowBtn : addToCartBtn;
      const label = $('[data-add-label]', buyNowBtn) || $('[data-cart-label]', addToCartBtn);
      const original = label ? label.textContent : '';
      if (btn) btn.disabled = true;
      if (label) label.textContent = '...';

      try {
        const formData = new FormData(productForm);
        const variantId = formData.get('id');
        if (!variantId) {
          throw new Error('Veuillez sélectionner une variante disponible.');
        }
        await addToCart(formData);

        if (pendingAction === 'buy-now') {
          // Buy Now: redirect to checkout
          if (label) label.textContent = '✓ Redirection...';
          window.location.href = '/checkout';
          return;
        }
        // Add to cart: open drawer
        if (messageEl) {
          messageEl.hidden = false;
          messageEl.textContent = '✓ Produit ajouté au panier';
          messageEl.className = 'product__form-message is-success';
        }
        if (label) label.textContent = '✓ Ajouté';
        setTimeout(openDrawer, 300);
      } catch (err) {
        console.error('[Zemoria] Product form error:', err);
        if (messageEl) {
          messageEl.hidden = false;
          messageEl.textContent = err.message || 'Erreur lors de l\'ajout au panier.';
          messageEl.className = 'product__form-message is-error';
        }
        if (label) label.textContent = '✗ Réessayer';
      } finally {
        setTimeout(() => {
          if (btn) btn.disabled = false;
          if (label) label.textContent = original;
          // Restore the Buy Now flag for the next click
          if (buyNowFlag) buyNowFlag.value = '/checkout';
        }, 2500);
      }
    });
  }

  /* ---------- Variant selector (PDP) ---------- */
  // When user clicks a variant button (e.g. "1 Litre" / "2 Litres"):
  //   1. Update the visual "selected" state on the buttons.
  //   2. Find the matching <option> in the hidden <select> using data-option1/2/3.
  //   3. Update the hidden <input name="id"> value — this is what gets submitted with the form.
  //   4. Update the displayed price to match the newly-selected variant.
  //   5. Update the main image if the variant has its own image.
  //   6. Stock-aware: disable the submit buttons if the selected variant is unavailable.
  //   7. Dispatch the standard Shopify `variant:change` event so bundle apps (BundleX, etc.)
  //      can react to variant changes.
  const variantSelect = $('[data-variant-select]');
  if (variantSelect) {
    const options = $$('[data-option-selector]');
    const priceNow = $('[data-price]');
    const priceCompare = $('[data-compare-price]');
    const mainImg = $('#product-main-img');
    const buyNowBtn = $('[data-buy-now]');
    const addToCartBtn = $('[data-add-to-cart-only]');
    const buyNowLabel = $('[data-add-label]', buyNowBtn);
    const addToCartLabel = $('[data-cart-label]', addToCartBtn);
    const formMessage = $('[data-form-message]');

    function syncVariant() {
      // Build the list of currently-selected option values, in order.
      const selectedValues = options.map(opt => {
        const active = $('.product__option-value--selected', opt);
        return active ? active.dataset.value : null;
      });

      // Find the matching <option> in the hidden select using data-option1/2/3 attributes.
      const allOptions = Array.from(variantSelect.options);
      const matchVariant = allOptions.find(opt => {
        const opts = [opt.dataset.option1, opt.dataset.option2, opt.dataset.option3].filter(v => v !== undefined && v !== '');
        if (opts.length !== selectedValues.length) return false;
        return opts.every((v, i) => v === selectedValues[i]);
      });

      if (!matchVariant) {
        // No matching variant at all
        if (formMessage) {
          formMessage.hidden = false;
          formMessage.textContent = 'Variante introuvable. Veuillez choisir une autre option.';
          formMessage.className = 'product__form-message is-error';
        }
        return;
      }

      // Update the hidden select value (for our internal tracking).
      variantSelect.value = matchVariant.value;

      // CRITICAL: Update the hidden <input name="id"> value — this is what Shopify
      // actually submits with the form. Bundle apps also read this input to know
      // which variant is currently selected.
      const variantIdInput = $('[data-variant-id-input]');
      if (variantIdInput) variantIdInput.value = matchVariant.value;

      // Update the price display.
      const price = matchVariant.dataset.price;
      const comparePrice = matchVariant.dataset.comparePrice;
      if (price && priceNow) priceNow.textContent = price;

      // Show/hide the compare-at price + sale badge.
      const priceWrapper = $('[data-price-wrapper]');
      if (priceWrapper) {
        const compareCents = parseInt(matchVariant.dataset.comparePriceCents || '0', 10);
        const priceCents = parseInt(matchVariant.dataset.priceCents || '0', 10);
        if (compareCents > priceCents && compareCents > 0) {
          if (priceCompare) priceCompare.textContent = comparePrice;
          priceWrapper.classList.add('is-sale');
          let saleBadge = $('.product__sale-badge', priceWrapper);
          if (!saleBadge) {
            saleBadge = document.createElement('span');
            saleBadge.className = 'product__sale-badge';
            saleBadge.textContent = 'Promo';
            priceWrapper.appendChild(saleBadge);
          }
          priceNow.classList.add('product__price-now--sale');
        } else {
          if (priceCompare) priceCompare.textContent = '';
          const saleBadge = $('.product__sale-badge', priceWrapper);
          if (saleBadge) saleBadge.remove();
          priceNow.classList.remove('product__price-now--sale');
        }
      }

      // Update the main image if the variant has its own.
      const img = matchVariant.dataset.image;
      if (img && mainImg && img !== 'undefined' && img !== '') {
        mainImg.src = img;
      }

      // CRITICAL: Stock-aware button states.
      // If the selected variant is unavailable, disable BOTH buttons and show
      // "Épuisé" — never silently fall back to a different variant.
      const isAvailable = matchVariant.dataset.available === 'true';
      if (buyNowBtn) buyNowBtn.disabled = !isAvailable;
      if (addToCartBtn) addToCartBtn.disabled = !isAvailable;
      if (buyNowLabel) buyNowLabel.textContent = isAvailable ? 'Acheter maintenant' : 'Épuisé';
      if (addToCartLabel) addToCartLabel.textContent = isAvailable ? 'Ajouter au panier' : 'Épuisé';

      // Show a small "out of stock" message under the buttons.
      if (formMessage) {
        if (!isAvailable) {
          formMessage.hidden = false;
          formMessage.textContent = 'Cette variante est épuisée. Sélectionnez l\'autre option.';
          formMessage.className = 'product__form-message is-error';
        } else {
          formMessage.hidden = true;
          formMessage.textContent = '';
          formMessage.className = 'product__form-message';
        }
      }

      // CRITICAL: Dispatch the standard Shopify variant:change event.
      // BundleX (and other apps) listen for this event to update their widget
      // when the customer switches variants.
      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant: {
            id: parseInt(matchVariant.value, 10),
            available: isAvailable,
            price: parseInt(matchVariant.dataset.priceCents || '0', 10),
            compareAtPrice: parseInt(matchVariant.dataset.comparePriceCents || '0', 10),
            option1: matchVariant.dataset.option1,
            option2: matchVariant.dataset.option2,
            option3: matchVariant.dataset.option3,
          }
        }
      }));

      // Also dispatch a `change` event on the form itself (some apps listen for this).
      const productForm = $('[data-product-form]');
      if (productForm) {
        productForm.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Wire up clicks on the option buttons.
    options.forEach(opt => {
      $$('[data-option-value]', opt).forEach(btn => {
        on(btn, 'click', () => {
          // Safety check: ignore clicks on disabled (out-of-stock) buttons.
          if (btn.disabled || btn.classList.contains('product__option-value--disabled')) {
            return;
          }
          $$('[data-option-value]', opt).forEach(b => b.classList.remove('product__option-value--selected'));
          btn.classList.add('product__option-value--selected');
          syncVariant();
        });
      });
    });

    // Run once on load to make sure everything is in sync.
    syncVariant();
  }

  /* ---------- Quantity steppers (PDP, cart page) ---------- */
  $$('[data-qty-stepper]').forEach(stepper => {
    const input = $('[data-qty-input]', stepper);
    const dec = $('[data-qty-decrement]', stepper);
    const inc = $('[data-qty-increment]', stepper);
    if (!input) return;
    on(dec, 'click', () => {
      const val = Math.max(parseInt(input.min || 0, 10), (parseInt(input.value, 10) || 0) - 1);
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    on(inc, 'click', () => {
      const val = (parseInt(input.value, 10) || 0) + 1;
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* ---------- Cart page line items: fast, serialised live update ---------- */
  const cartPage = $('[data-cart-page]');
  if (cartPage) {
    const cartInputs = $$('[data-qty-input][data-line]', cartPage);
    const cartStatus = $('[data-cart-status]', cartPage);
    const checkoutButton = $('[data-checkout-button]', cartPage);
    const updateButton = $('.cart-page__update-btn', cartPage);
    let cartStatusTimer;

    if (updateButton) updateButton.hidden = true;

    function setCartStatus(message, isError) {
      if (!cartStatus) return;
      clearTimeout(cartStatusTimer);
      cartStatus.textContent = message || '';
      cartStatus.classList.toggle('is-error', Boolean(isError));
    }

    function cartInputIsBusy(input) {
      return Boolean(input._cartTimer || input._cartUpdating);
    }

    function refreshCheckoutState() {
      if (!checkoutButton) return;
      const busy = cartInputs.some(cartInputIsBusy);
      checkoutButton.disabled = busy;
      checkoutButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    function updateQuantityLabel(input, quantity) {
      const row = input.closest('[data-cart-line]');
      const label = row && $('[data-qty-label]', row);
      const offerReset = row && $('[data-offer-reset]', row);
      if (offerReset) offerReset.hidden = quantity === 2;
      if (!label) return;
      const unitLitres = parseFloat(row.dataset.unitLitres || '0');
      const litres = Math.round(quantity * unitLitres * 10) / 10;
      if (quantity === 0) {
        label.textContent = 'Suppression…';
      } else if (quantity === 2 && unitLitres === 2) {
        label.textContent = 'Offre 4 L · 2 bidons · livraison offerte';
      } else if (quantity === 1 && unitLitres === 2) {
        label.textContent = 'Format 2 L · 1 bidon';
      } else {
        label.textContent = quantity + ' bidon' + (quantity > 1 ? 's' : '') + (unitLitres ? ' · ' + litres + ' L' : '');
      }
    }

    async function flushCartInput(input) {
      if (input._cartUpdating) return;
      const requested = Math.max(0, parseInt(input._cartDesiredQuantity, 10) || 0);
      const committed = Math.max(0, parseInt(input.dataset.committedQuantity, 10) || 0);
      if (requested === committed) {
        refreshCheckoutState();
        return;
      }

      const line = parseInt(input.dataset.line, 10);
      const row = input.closest('[data-cart-line]');
      input._cartUpdating = true;
      if (row) row.classList.add('is-updating');
      setCartStatus('Mise à jour du panier…', false);
      refreshCheckoutState();

      try {
        const cart = await changeCart(line, requested);
        if (requested === 0) {
          window.location.reload();
          return;
        }

        input.dataset.committedQuantity = String(requested);
        const item = cart.items[line - 1];
        const totalEl = row && $('[data-line-total]', row);
        if (totalEl && item) totalEl.innerHTML = formatMoney(item.final_line_price);
        updateCount(cart.item_count);
        updateSubtotal(cart.total_price);
      } catch (error) {
        console.error(error);
        const rollback = Math.max(0, parseInt(input.dataset.committedQuantity, 10) || 0);
        input._cartDesiredQuantity = rollback;
        input.value = rollback;
        updateQuantityLabel(input, rollback);
        setCartStatus('Impossible de modifier le panier. Réessayez.', true);
      } finally {
        input._cartUpdating = false;
        if (row) row.classList.remove('is-updating');

        const desired = Math.max(0, parseInt(input._cartDesiredQuantity, 10) || 0);
        const latestCommitted = Math.max(0, parseInt(input.dataset.committedQuantity, 10) || 0);
        if (desired !== latestCommitted) {
          setTimeout(() => flushCartInput(input), 0);
        } else {
          setCartStatus('Panier mis à jour', false);
          cartStatusTimer = setTimeout(() => setCartStatus('', false), 1400);
        }
        refreshCheckoutState();
      }
    }

    cartInputs.forEach(input => {
      input.dataset.committedQuantity = input.value;
      input._cartDesiredQuantity = parseInt(input.value, 10) || 0;
      updateQuantityLabel(input, input._cartDesiredQuantity);

      on(input, 'change', () => {
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        input.value = quantity;
        input._cartDesiredQuantity = quantity;
        updateQuantityLabel(input, quantity);
        clearTimeout(input._cartTimer);
        setCartStatus('Mise à jour…', false);
        input._cartTimer = setTimeout(() => {
          input._cartTimer = null;
          flushCartInput(input);
        }, 180);
        refreshCheckoutState();
      });
    });

    $$('[data-offer-reset]', cartPage).forEach(button => {
      on(button, 'click', () => {
        const line = parseInt(button.dataset.line, 10);
        const input = $(`[data-qty-input][data-line="${line}"]`, cartPage);
        if (!input) return;
        input.value = 2;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- Gallery thumbnails ---------- */
  const thumbs = $$('[data-thumb]');
  thumbs.forEach(thumb => {
    on(thumb, 'click', () => {
      const mainImg = $('#product-main-img');
      if (!mainImg) return;
      mainImg.src = thumb.dataset.thumb;
      thumbs.forEach(t => t.classList.remove('product__thumb--active'));
      thumb.classList.add('product__thumb--active');
    });
  });

  /* ---------- Mobile menu ---------- */
  const mobileMenu = $('[data-mobile-menu]');
  $$('[data-menu-toggle]').forEach(btn => {
    on(btn, 'click', () => {
      if (!mobileMenu) return;
      const isOpen = mobileMenu.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      mobileMenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });
  });

  /* ---------- Drawer toggles ---------- */
  $$('[data-cart-drawer-toggle]').forEach(btn => {
    on(btn, 'click', (e) => {
      e.preventDefault();
      if (drawer && drawer.classList.contains('is-open')) closeDrawer();
      else openDrawer();
    });
  });

  /* ---------- Collection page sort ---------- */
  const sortBy = $('[data-sort-by]');
  if (sortBy) {
    on(sortBy, 'change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortBy.value);
      window.location.href = url.toString();
    });
  }

  /* ---------- Escape key closes overlays ---------- */
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') {
      if (drawer && drawer.classList.contains('is-open')) closeDrawer();
      if (mobileMenu && mobileMenu.classList.contains('is-open')) {
        mobileMenu.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }
  });

  /* ---------- HTML escape helpers ---------- */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  function escapeAttr(str) { return escapeHtml(str); }

  /* ---------- Scroll Reveal (IntersectionObserver) ---------- */
  const revealEls = $$('[data-reveal], [data-reveal-item]');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    revealEls.forEach(el => observer.observe(el));
  } else {
    // Fallback: show everything immediately
    revealEls.forEach(el => el.classList.add('is-revealed'));
  }

  /* ---------- Hero parallax (gentle, no opacity fade) ---------- */
  const heroParallax = $('[data-hero-parallax]');
  if (heroParallax && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let ticking = false;
    on(window, 'scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const heroHeight = heroParallax.offsetHeight || window.innerHeight;
          // Only parallax while hero is in view — gentle background drift only
          if (scrollY < heroHeight) {
            heroParallax.style.transform = `translateY(${scrollY * 0.25}px)`;
          }
          ticking = false;
        }, { passive: true });
        ticking = true;
      }
    });
  }

  /* ---------- Initial cart count ---------- */
  fetchCart().then(cart => updateCount(cart.item_count)).catch(() => {});

})();
